import * as React from 'react';
import Button from '@material-ui/core/Button';
import GoogleMapReact, { Coords } from 'google-map-react';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ImportsGrid } from '../components/imports-grid.component';
import { ImportLines } from '../interfaces/import-lines';
import {
    clearImportLines,
    getImportLines,
    importLines$,
    loadingImportLines$,
} from '../services/import-types.service';
import { GOOGLE_MAPS_API_KEY } from '../../shared/constants';

interface Props {}
interface State {
    defaultCenter: Coords;
    importLines: ImportLines | null;
    loadingImportLines: boolean;
}

require('./import.page.scss');
export class ImportPage extends React.Component<Props, State> {
    private destroyed$ = new Subject<void>();
    private googleMapRef = null as any;
    /**
     * Import actuellement dessiné. Sert à savoir quoi recharger quand la carte bouge, et à ne
     * recentrer qu'au premier affichage.
     */
    private displayedImportTypeId?: number;

    constructor(props: Props) {
        super(props);

        this.state = {
            defaultCenter: {
                // Paris
                lat: 48.854954,
                lng: 2.346404,
            },
            importLines: null,
            loadingImportLines: false,
        } as State;
    }

    public render() {
        const { defaultCenter } = this.state;

        return (
            <div className='import page'>
                <ImportsGrid />
                { this.renderMapCaption() }
                <div className='map'>
                    {/* Ni zoom ni center contrôlés : la carte est déplacée à la main par
                        l'utilisateur, les repasser en props les remettrait à zéro à chaque rendu */}
                    <GoogleMapReact
                        bootstrapURLKeys={{ key: GOOGLE_MAPS_API_KEY }}
                        defaultZoom={9}
                        style={{ width: '100%', height: '100%' }}
                        defaultCenter={defaultCenter}
                        yesIWantToUseGoogleMapApiInternals={true}
                        onGoogleApiLoaded={this.handleApiLoaded}
                        onChange={this.handleMapChange} />
                </div>
            </div>
        );
    }

    public componentDidMount() {
        this.subscribeToImportLines();
        this.subscribeToLoadingImportLines();
    }

    public componentWillUnmount() {
        this.destroyed$.next();

        // Sans ce reset, revenir sur la page redessinerait les lignes du dernier import consulté
        clearImportLines();
    }

    private renderMapCaption = () => {
        const { importLines, loadingImportLines } = this.state;

        if (loadingImportLines) {
            return <p className='map-caption'>Chargement des lignes…</p>;
        }

        if (!importLines) {
            return (
                <p className='map-caption'>
                    Cliquer sur l’icône carte d’un import pour afficher ses lignes.
                </p>
            );
        }

        const importName = `${importLines.name} ${importLines.lineType}`;

        if (importLines.returnedFeatures === 0) {
            return (
                <p className='map-caption'>
                    {`${importName} : aucune ligne dans la zone affichée. Déplacer ou dézoomer la carte.`}
                    { this.renderClearButton() }
                </p>
            );
        }

        return (
            <p className='map-caption'>
                {`${importName} : ${importLines.returnedFeatures.toLocaleString('fr-FR')} lignes affichées`}
                { importLines.truncated
                    && ' — trop de lignes dans cette zone pour toutes les afficher, zoomer pour voir le détail' }
                { this.renderClearButton() }
            </p>
        );
    }

    private renderClearButton = () => (
        <Button color='primary' size='small' onClick={this.handleOnClearLines}>Effacer</Button>
    )

    private handleApiLoaded = ({ map }: any) => {
        this.googleMapRef = map;
        if (map === null) return;

        map.data.setStyle(() => {
            const color = this.lineColor();

            return /** @type {!google.maps.Data.StyleOptions} */({
                fillColor: color,
                strokeColor: color,
                strokeWeight: 2,
            });
        });

        // Les lignes peuvent être arrivées avant que la carte ne soit prête
        this.drawLines();
    }

    /** Mêmes couleurs que la carte de la page Communes, pour ne pas dérouter d'une page à l'autre */
    private lineColor = (): string => {
        const { importLines } = this.state;

        switch (importLines && importLines.lineType) {
            case 'THT-HTB':
                return 'black';
            case 'HTA':
                return 'red';
            case 'BT':
                return 'blue';
            default:
                return 'yellow';
        }
    }

    private drawLines = () => {
        if (!this.googleMapRef) return;

        const data = this.googleMapRef.data;

        // Les features sont collectées avant d'être retirées : appeler remove() pendant le forEach
        // modifierait la collection en cours d'itération
        const features: any[] = [];
        data.forEach((feature: any) => features.push(feature));
        features.forEach((feature: any) => data.remove(feature));

        const { importLines } = this.state;
        if (!importLines) return;

        data.addGeoJson(importLines.featureCollection);
    }

    private handleMapChange = ({ bounds }: any) => {
        if (!bounds || this.displayedImportTypeId === undefined) return;

        // onChange n'est émis qu'une fois la carte stabilisée (option debounced par défaut),
        // pas à chaque image du déplacement
        getImportLines(this.displayedImportTypeId, {
            north: bounds.ne.lat,
            south: bounds.sw.lat,
            east: bounds.ne.lng,
            west: bounds.sw.lng,
        });
    }

    private handleOnClearLines = (event: any) => {
        event.preventDefault();

        clearImportLines();
    }

    private subscribeToImportLines() {
        importLines$().pipe(
            takeUntil(this.destroyed$),
        )
            .subscribe(importLines => {
                this.setState({ importLines }, () => {
                    this.drawLines();

                    if (!importLines) {
                        this.displayedImportTypeId = undefined;
                        return;
                    }

                    const isNewImport = importLines.importTypeId !== this.displayedImportTypeId;
                    this.displayedImportTypeId = importLines.importTypeId;

                    // Premier affichage : la carte n'a aucune raison de regarder la bonne zone, on
                    // la place sur les données. Le déplacement déclenche onChange, donc un second
                    // appel borné à l'emprise réellement visible.
                    if (isNewImport && importLines.center && this.googleMapRef) {
                        this.googleMapRef.setCenter(importLines.center);
                    }
                });
            });
    }

    private subscribeToLoadingImportLines() {
        loadingImportLines$().pipe(
            takeUntil(this.destroyed$),
        )
            .subscribe(loadingImportLines => {
                this.setState({ loadingImportLines });
            });
    }
}
